/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import FitPill from './FitPill';
import JobTable from './JobTable';
import KmDelta from './KmDelta';
import { SKILL_SCORE_GROUPS } from '../calculator';

function CandidateRow({ candidate: c, ofscNr, expanded, onToggle, onMove, onShowMap }) {
  return (
    <>
      <tr className={`cand-row${expanded ? ' expanded' : ''}`} onClick={onToggle}>
        <td>
          <span className="candidate-caret">{expanded ? '▼' : '▶'}</span>
          {c.targetTechName}
        </td>
        <td>{c.target.jobCountBefore} randevu · {c.target.servicePaperCountBefore} fiş</td>
        <td><FitPill fit={c.fit} /></td>
        <td><KmDelta value={c.combinedKmDelta} /></td>
        <td>
          {Math.max(0, c.donor.etaViolationCountDelta)
            + Math.max(0, c.target.etaViolationCountDelta)}
        </td>
      </tr>
      <tr className={`after-detail-row${expanded ? ' expanded' : ''}`}>
        <td colSpan={5} className="after-detail-cell">
          <div className={`after-detail${expanded ? ' open' : ''}`}>
            {expanded && (
              <>
                <div className="candidate-target-heading">
                  {onMove && (
                    <>
                      <button
                        type="button"
                        className="candidate-move-button"
                      onClick={() => onMove(ofscNr, c.targetTechCode)}
                    >
                      <DriveFileMoveOutlinedIcon aria-hidden="true" />
                      Taşı
                    </button>
                      <span className="candidate-move-arrow" aria-hidden="true">→</span>
                    </>
                  )}
                  <h4>
                    <span className="candidate-target-tech-name">{c.target.techName}</span>
                    <span className="candidate-target-caption">taşımadan sonra (hedef)</span>
                  </h4>
                  {onShowMap && (
                    <button
                      type="button"
                      className="candidate-map-button"
                      title="Simülasyon rotasını haritada göster"
                      aria-label={`${c.target.techName} simülasyon rotasını haritada göster`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onShowMap({
                          ofscNr,
                          targetTechCode: c.targetTechCode,
                          targetTechName: c.target.techName,
                          routeAfter: c.target.routeAfter,
                          kmDelta: c.combinedKmDelta,
                        });
                      }}
                    >
                      <MapOutlinedIcon aria-hidden="true" />
                      <span>Haritada göster</span>
                    </button>
                  )}
                </div>
                <JobTable
                  jobs={c.target.routeAfter}
                  highlightOfscNr={ofscNr}
                  highlightEffect={c.combinedKmDelta < 0 ? 'positive' : c.combinedKmDelta > 0 ? 'negative' : 'neutral'}
                />
              </>
            )}
          </div>
        </td>
      </tr>
    </>
  );
}

function CandidateTable({ candidates, ofscNr, expandedSet, toggle, onMove, onShowMap }) {
  return (
    <table className="candidate-table">
      <thead>
        <tr>
          <th>Teknisyen</th><th>Randevu / Fiş</th><th>Durum</th><th>Km Etkisi</th>
          <th>İhlal</th>
        </tr>
      </thead>
      <tbody>
        {candidates.map(c => (
          <CandidateRow
            key={c.targetTechCode}
            candidate={c}
            ofscNr={ofscNr}
            expanded={expandedSet.has(c.targetTechCode)}
            onToggle={() => toggle(c.targetTechCode)}
            onMove={onMove}
            onShowMap={onShowMap}
          />
        ))}
      </tbody>
    </table>
  );
}

// Seçilen işi başka teknisyenlere taşımanın sıralı aday listesi (sağ panel). Bir satıra
// tıklandığında Mevcut/hedef için "taşımadan sonra" detay tabloları açılır/kapanır.
export default function CandidatesPanel({ data, onMove, onShowMap }) {
  const [expandedSet, setExpandedSet] = useState(() => new Set());
  const [collapsedGroupSet, setCollapsedGroupSet] = useState(() => new Set());

  if (!data) {
    return <div className="empty">Taşımak istediğiniz işi soldan seçin.</div>;
  }

  const job = data.job;
  const reasonLabel = job.appReason || job.products || `Uzmanlık ${data.requiredSkillId}`;
  const donorScoreGroup = SKILL_SCORE_GROUPS.find(
    group => group.score === Number(data.donorSkillScore),
  );
  const header = (
    <div className="selected-job-card">
      <div className="title">{job.customer || 'Müşteri'} — İş #{data.ofscNr}</div>
      Mevcut teknisyen: <b>{data.donorTechName}</b> ·
      {' '}Randevu: {job.appointmentTime}–{job.appointmentTimeEnd} ·
      {' '}Mahalle: {job.neighbourhood || '-'} ·
      {' '}Başvuru Nedeni: {job.appReason || job.products || '-'} ·
      {' '}Uzmanlık: {data.requiredSkillId || '-'}
      {donorScoreGroup && <> · Mevcut uygunluk: <b>{donorScoreGroup.label}</b></>}
    </div>
  );

  if (!data.candidates.length) {
    return <>{header}<div className="empty">Uygun aday teknisyen bulunamadı.</div></>;
  }

  function toggle(targetTechCode) {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(targetTechCode)) next.delete(targetTechCode); else next.add(targetTechCode);
      return next;
    });
  }

  function toggleGroup(score) {
    const groupKey = String(score);
    setCollapsedGroupSet((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
      return next;
    });
  }

  const scoreGroups = SKILL_SCORE_GROUPS
    .map(group => ({
      ...group,
      candidates: data.candidates.filter(candidate => Number(candidate.skillScore) === group.score),
    }));
  const knownScores = new Set(SKILL_SCORE_GROUPS.map(group => group.score));
  const otherCandidates = data.candidates.filter(
    candidate => !knownScores.has(Number(candidate.skillScore)),
  );
  if (otherCandidates.length) {
    scoreGroups.push({
      score: 'other',
      label: 'Score Bulunamadı',
      description: 'bu teknisyen için ilgili uzmanlık score bilgisi bulunamadı',
      candidates: otherCandidates,
    });
  }

  return (
    <>
      {header}
      {scoreGroups.map((group) => {
        const groupKey = String(group.score);
        const collapsed = collapsedGroupSet.has(groupKey);
        const toggleCurrentGroup = () => toggleGroup(group.score);
        return (
          <section className={`skill-score-group skill-score-${group.score}`} key={group.score}>
            <div
              className="skill-score-heading"
              role="button"
              tabIndex={0}
              aria-expanded={!collapsed}
              onClick={toggleCurrentGroup}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleCurrentGroup();
                }
              }}
            >
              <span className="skill-score-caret">{collapsed ? '▶' : '▼'}</span>
              <span className="skill-score-label">{group.label}</span>
              <span><b>{reasonLabel}</b> başvuru nedeni için {group.description}.</span>
              <span className="skill-score-count">{group.candidates.length} teknisyen</span>
            </div>
            {!collapsed && (
              <div className="skill-score-body">
                <CandidateTable
                  candidates={group.candidates}
                  ofscNr={data.ofscNr}
                  expandedSet={expandedSet}
                  toggle={toggle}
                  onMove={onMove}
                  onShowMap={onShowMap}
                />
                {group.candidates.length === 0 && (
                  <div className="skill-score-empty">Bu grupta teknisyen bulunmuyor.</div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}
